import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import moment from 'moment'
import { Preloader } from '../../../../shared/components/cards/preloader/Preloader'
import { useAppDispatch } from '../../../../app/store/hooks/Redux'
import { hideModal } from '../../../../app/store/reducers/AppSlice'
import { useGetTmInfringementsViewReplyQuery } from '../../../../app/api'
import { IconWarningCircle } from '../../../../shared/components/icons/IconWarningCircle'
import { Badge } from '../../../../lib/ui/badge/component/badge/Badge'
import {
  REGISTRATION_REJECTION_REASONS,
  toRejectionReason,
} from '../../model/types/RejectionReason.type'
import { IconArrowBack } from '../../../../shared/components/icons/IconArrowBack'
import { IconError } from '../../../../shared/components/icons/IconError'
import { TComplaintStatus } from '../../model/types/ComplaintStatus.type'

const RECEIVED_AT_FORMAT = 'DD MMM YYYY, HH:mm'

const formatReceivedAt = (receivedAt: string) => {
  const date = moment(receivedAt)

  return date.isValid() ? date.format(RECEIVED_AT_FORMAT) : receivedAt
}

const VERDICT_COLORS = {
  rejected: {
    background: '#FF245014',
    accent: '#FF5778',
    reason: '#FF5778',
    iconBackground: '#FF245014',
  },
  disappeared: {
    background: '#8D7CA71F',
    accent: '#A89ABB',
    reason: '#A89ABB',
    iconBackground: '#FF245014',
  },
}

interface TrademarkInfringementsReplyModalProps {
  projectId: number
  caseId: number
}

export const TrademarkInfringementsReplyModal = ({
  projectId,
  caseId,
}: TrademarkInfringementsReplyModalProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const { data, isSuccess, isLoading } = useGetTmInfringementsViewReplyQuery(
    {
      projectId,
      caseId,
    },
    { skip: !projectId || !caseId },
  )

  useEffect(() => {
    if (isSuccess && !data?.data) {
      dispatch(hideModal())
    }
  }, [data])

  const replies = data?.data?.replies || []
  const replyReason = toRejectionReason(data?.data?.reply_type)
  // в openapi стенда disappeared ещё нет, контракт — по ТЗ
  const status = data?.data?.complaint_status as TComplaintStatus | undefined
  const verdict =
    status === 'disappeared' || (status === 'rejected' && replyReason === 'ad_not_found')
      ? 'disappeared'
      : status === 'rejected'
        ? 'rejected'
        : null
  // у Disappeared причина всегда «Ad not found», как в RejectionReasonLabel
  const reason = verdict === 'disappeared' ? 'ad_not_found' : replyReason

  return (
    <div className={'flex min-h-0 flex-1 flex-col'}>
      <div
        className={
          'flex flex-col gap-[16px] border-b-[3px] border-b-[#00326D]/[.02] px-[24px] py-[16px]'
        }
      >
        <h3 className={'flex items-center gap-[12px] pr-[40px] text-[20px] font-medium'}>
          <div
            className={
              'flex h-[32px] w-[32px] shrink-0 items-center justify-center rounded-[8px] border border-[#E5E1EA]'
            }
          >
            <div className={'w-[15px]'}>
              <IconArrowBack color={'#6684A7'} />
            </div>
          </div>
          {t('pages.trademarkInfringements.replyPreview')}
        </h3>
        {verdict && (
          <div
            className={'flex gap-[12px] rounded-[12px] p-[16px]'}
            style={{ backgroundColor: VERDICT_COLORS[verdict].background }}
          >
            <div
              className={
                'flex h-[24px] w-[24px] shrink-0 items-end justify-center rounded-full p-[4px] pt-[2px]'
              }
              style={{ backgroundColor: VERDICT_COLORS[verdict].iconBackground }}
            >
              <IconError color={VERDICT_COLORS[verdict].accent} />
            </div>
            <div className={'flex flex-col gap-[8px] text-[14px] leading-[20px]'}>
              <div
                className={'flex flex-col text-[15px] leading-[20px]'}
                style={{ color: VERDICT_COLORS[verdict].reason }}
              >
                <p className={'font-semibold'} style={{ color: VERDICT_COLORS[verdict].accent }}>
                  {t(`pages.trademarkInfringements.replyVerdict.${verdict}`)}
                </p>
                <p>{t(`pages.trademarkInfringements.rejectionReasons.${reason}.label`)}</p>
              </div>
              <p className={'text-[#8E7CA7]'}>
                {t(`pages.trademarkInfringements.rejectionReasons.${reason}.tooltip`)}
                {REGISTRATION_REJECTION_REASONS.includes(reason) && (
                  <>
                    {' '}
                    <Link
                      className={'text-focus transition-opacity duration-150 hover:opacity-80'}
                      to={`/projects/${projectId}/trademarks`}
                      onClick={() => dispatch(hideModal())}
                    >
                      {t('pages.trademarkInfringements.updateTrademarkRegistration')}
                    </Link>
                  </>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      {isLoading ? (
        <div className={'relative flex flex-1 items-center justify-center p-[24px]'}>
          <Preloader />
        </div>
      ) : (
        <div className={'max-h-[480px] min-h-0 flex-1 divide-y divide-[#E5E1EA] overflow-y-auto'}>
          {replies.map((reply, i) => (
            <div key={i} className={'flex flex-col gap-[16px] p-[24px]'}>
              <div className={'flex items-start justify-between gap-[16px]'}>
                <p className={'break-words text-[16px] font-medium text-focus-500'}>
                  {reply.subject}
                </p>
                <div className={'flex shrink-0 items-center gap-[8px]'}>
                  {i === 0 && replies.length > 1 && (
                    <Badge color={'grey'}>{t('pages.trademarkInfringements.latestReply')}</Badge>
                  )}
                  {reply.received_at && (
                    <span className={'whitespace-nowrap text-[14px] text-[#6684A7]'}>
                      {formatReceivedAt(reply.received_at)}
                    </span>
                  )}
                </div>
              </div>
              {!!reply.body && (
                <p
                  className={'whitespace-pre-line break-words text-[16px] text-focus-500'}
                  dangerouslySetInnerHTML={{ __html: reply.body }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
